import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import useHelpTarget from '../../hooks/useHelpTarget';

/**
 * Two-axis classification editor.
 *
 * Level 1 (MediaKind) — read-only, set deterministically at ingest.
 * Level 2 (Purpose)   — editable dropdown filtered to the doc's MediaKind.
 *
 * Auto-saves on change with optimistic UI; rolls back on error.
 */

function humanize(s) {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function KV({ label, children }) {
  return (
    <div className="dd-kv">
      <span className="dd-kv-label">{label}</span>
      <span className="dd-kv-value">{children ?? '—'}</span>
    </div>
  );
}

const MEDIA_KIND_LABELS = {
  document: 'Document', spreadsheet: 'Spreadsheet', presentation: 'Presentation',
  image: 'Image', video: 'Video', audio: 'Audio',
  archive: 'Archive', data_export: 'Data Export', unknown: 'Unknown',
};

export default function ClassificationEditor({ doc, onLocalChange }) {
  const purposeHelpRef = useHelpTarget({
    slug: 'interpreting-document-classifications',
    label: 'Document classification (Kind + Purpose)',
    question: 'What do Kind and Purpose mean, and how do I reclassify a document?',
  });
  const [vocab, setVocab] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const reasonRef = useRef(null);

  const caseId = doc.case_id;
  const mediaKind = doc.media_kind || null;

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    const qs = mediaKind ? `?media_kind=${encodeURIComponent(mediaKind)}` : '';
    api.get(`/documents/case/${caseId}/classification-vocabulary${qs}`)
      .then(d => { if (!cancelled) setVocab(d); })
      .catch(() => { if (!cancelled) setVocab({ groups: [], purposes_by_group: {} }); });
    return () => { cancelled = true; };
  }, [caseId, mediaKind]);

  // Prefer new field; fall back to legacy for pre-migration documents
  const currentPurpose = doc.document_purpose || doc.document_type || '';
  const currentGroup = doc.document_purpose_group || doc.document_category || '';
  const isManual = ['manual', 'human_manual'].includes(doc.classification_source);
  const confidence = doc.purpose_confidence ?? null;
  const autoWas = doc.auto_purpose_was || null;

  async function patchAndUpdate(payload, optimisticDoc) {
    setSaveState('saving');
    setSaveError(null);
    onLocalChange(optimisticDoc);
    try {
      const updated = await api.patch(`/documents/${doc.id}`, payload);
      onLocalChange(updated);
      setSaveState('saved');
      setTimeout(() => setSaveState(prev => prev === 'saved' ? 'idle' : prev), 1500);
    } catch (err) {
      setSaveState('error');
      setSaveError(err.message || 'Save failed');
      try {
        const fresh = await api.get(`/documents/${doc.id}`);
        onLocalChange(fresh);
      } catch { /* swallow */ }
    }
  }

  function handlePurposeChange(e) {
    const newPurpose = e.target.value;
    if (newPurpose === currentPurpose) return;
    let newGroup = currentGroup;
    if (vocab?.purposes_by_group) {
      for (const [grp, items] of Object.entries(vocab.purposes_by_group)) {
        if (items.some(p => p.key === newPurpose)) { newGroup = grp; break; }
      }
    }
    const optimistic = {
      ...doc,
      document_purpose: newPurpose,
      document_purpose_group: newGroup,
      document_type: newPurpose,
      document_category: newGroup,
      classification_source: 'human_manual',
    };
    setPendingChange({ newPurpose, newGroup, optimistic });
    setReasonText('');
    setTimeout(() => reasonRef.current?.focus(), 50);
  }

  function confirmReclassify() {
    if (!pendingChange) return;
    const { newPurpose, optimistic } = pendingChange;
    const payload = {
      document_purpose: newPurpose,
      reason: reasonText.trim() || null,
    };
    setPendingChange(null);
    setReasonText('');
    patchAndUpdate(payload, optimistic);
  }

  function cancelReclassify() {
    setPendingChange(null);
    setReasonText('');
  }

  const saveBadge =
    saveState === 'saving' ? <span className="dd-save-state" title="Saving…">⏳</span>
    : saveState === 'saved' ? <span className="dd-save-state dd-save-ok" title="Saved">✓</span>
    : saveState === 'error' ? <span className="dd-save-state dd-save-err" title={saveError || 'Save failed'}>⚠</span>
    : null;

  const lowConfidence = confidence !== null && confidence < 0.7;
  const confidencePct = confidence !== null ? Math.round(confidence * 100) : null;

  return (
    <>
      {mediaKind && (
        <KV label="Kind">
          <span className="dd-mediakind-badge" title="Set at ingest — not editable">
            {MEDIA_KIND_LABELS[mediaKind] || mediaKind}
          </span>
        </KV>
      )}

      {currentGroup && (
        <KV label="Group">{currentGroup}</KV>
      )}

      <div className="dd-kv" ref={purposeHelpRef}>
        <span className="dd-kv-label">
          Purpose
          {isManual && <span className="dd-manual-badge" title="Manually overridden">manual</span>}
          {saveBadge}
          {lowConfidence && (
            <span
              className="dd-confidence-badge dd-confidence-low"
              title={autoWas ? `Model picked "${autoWas}" — low confidence` : 'Low confidence classification'}
            >
              {confidencePct}%
            </span>
          )}
        </span>
        <span className="dd-kv-value">
          {!vocab ? (
            <span>{humanize(currentPurpose) || '—'}</span>
          ) : (
            <select
              className="dd-select"
              value={currentPurpose}
              onChange={handlePurposeChange}
              disabled={saveState === 'saving'}
            >
              {!currentPurpose && <option value="">— select —</option>}
              {currentPurpose && !Object.values(vocab.purposes_by_group || {}).flat().some(p => p.key === currentPurpose) && (
                <option value={currentPurpose}>{humanize(currentPurpose)}</option>
              )}
              {(vocab.groups || []).map(group => {
                const items = vocab.purposes_by_group?.[group] || [];
                if (!items.length) return null;
                return (
                  <optgroup key={group} label={group}>
                    {items.map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          )}
        </span>
      </div>

      {pendingChange && (
        <div className="dd-reason-backdrop" onClick={cancelReclassify}>
          <div className="dd-reason-dialog" onClick={e => e.stopPropagation()}>
            <p className="dd-reason-title">Why are you changing this?</p>
            <p className="dd-reason-meta">
              <span className="dd-reason-from">{humanize(currentPurpose) || 'Unknown'}</span>
              {' → '}
              <span className="dd-reason-to">{humanize(pendingChange.newPurpose)}</span>
            </p>
            <textarea
              ref={reasonRef}
              className="dd-reason-textarea"
              placeholder="Optional — helps the system learn from your corrections"
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) confirmReclassify(); }}
              rows={3}
            />
            <div className="dd-reason-actions">
              <button className="dd-reason-cancel" onClick={cancelReclassify}>Cancel</button>
              <button className="dd-reason-confirm" onClick={confirmReclassify}>Save change</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
